(function () {
  const STORAGE_PREFIX = "aldo-plp-state:";
  const MAX_STATE_AGE = 30 * 60 * 1000; // 30 minutes

  /*
   * ============================================================
   * HELPERS
   * ============================================================
   */

  function getStorageKey() {
    return STORAGE_PREFIX + window.location.pathname + window.location.search;
  }

  function normalizeProductUrl(url) {
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.pathname;
    } catch (e) {
      return url || "";
    }
  }

  function getState() {
    try {
      const raw = sessionStorage.getItem(getStorageKey());

      if (!raw) return null;

      const state = JSON.parse(raw);

      if (!state || !state.timestamp) {
        return null;
      }

      // Ignore very old saved positions
      if (Date.now() - state.timestamp > MAX_STATE_AGE) {
        sessionStorage.removeItem(getStorageKey());
        return null;
      }

      return state;
    } catch (e) {
      console.warn("PLP state read error:", e);
      return null;
    }
  }

  function saveState(data) {
    try {
      const current = getState() || {};

      const state = Object.assign({}, current, data, {
        timestamp: Date.now()
      });

      sessionStorage.setItem(
        getStorageKey(),
        JSON.stringify(state)
      );
    } catch (e) {
      console.warn("PLP state save error:", e);
    }
  }

  function isBackForwardNavigation() {
    try {
      const entries = performance.getEntriesByType
        ? performance.getEntriesByType("navigation")
        : [];

      if (
        entries &&
        entries.length &&
        entries[0].type === "back_forward"
      ) {
        return true;
      }

      // Older Safari fallback
      if (
        performance.navigation &&
        performance.navigation.type === 2
      ) {
        return true;
      }
    } catch (e) {}

    return false;
  }

  /*
   * ============================================================
   * FIND PRODUCT CARD
   * ============================================================
   */

  function findProductCard(state) {
    const grid = document.querySelector("[data-product-grid]");

    if (!grid || !state) {
      return null;
    }

    /*
     * First use the product handle.
     *
     * Your card-product.liquid already contains:
     *
     * data-wishlist-item="{{ card_product.handle }}"
     */
    if (state.productHandle) {
      try {
        const cards = grid.querySelectorAll(
          "[data-wishlist-item]"
        );

        for (const card of cards) {
          if (
            card.getAttribute("data-wishlist-item") ===
            state.productHandle
          ) {
            return card;
          }
        }
      } catch (e) {}
    }

    /*
     * Fallback: find product by URL.
     */
    if (state.productUrl) {
      const links = grid.querySelectorAll(
        'a[href*="/products/"]'
      );

      for (const link of links) {
        if (
          normalizeProductUrl(link.href) ===
          state.productUrl
        ) {
          return link.closest(
            ".card-wrapper, .product-card-wrapper, .grid__item"
          );
        }
      }
    }

    return null;
  }

  /*
   * ============================================================
   * RESTORE SCROLL POSITION
   * ============================================================
   */

  function restorePosition(state) {
    if (!state) return false;

    const card = findProductCard(state);

    /*
     * Best method:
     *
     * Restore the clicked product to the same position
     * inside the viewport.
     */
    if (card) {
      const rect = card.getBoundingClientRect();

      if (
        state.cardViewportTop !== null &&
        state.cardViewportTop !== undefined &&
        Number.isFinite(Number(state.cardViewportTop))
      ) {
        const wantedTop = Number(state.cardViewportTop);

        const difference =
          rect.top - wantedTop;

        window.scrollBy(0, difference);

        return true;
      }

      card.scrollIntoView({
        block: "center",
        behavior: "auto"
      });

      return true;
    }

    /*
     * Fallback to exact scrollY.
     */
    if (
      state.scrollY !== null &&
      state.scrollY !== undefined &&
      Number.isFinite(Number(state.scrollY))
    ) {
      window.scrollTo(
        0,
        Number(state.scrollY)
      );

      return true;
    }

    return false;
  }

  /*
   * ============================================================
   * PRODUCT CLICK TRACKING
   * ============================================================
   */

  function setupProductClickTracking() {
    if (window.__aldoPlpClickTracking) {
      return;
    }

    window.__aldoPlpClickTracking = true;

    document.addEventListener(
      "click",
      function (event) {
        const link = event.target.closest(
          'a[href*="/products/"]'
        );

        if (!link) {
          return;
        }

        const grid = link.closest(
          "[data-product-grid]"
        );

        if (!grid) {
          return;
        }

        const card = link.closest(
          ".card-wrapper, .product-card-wrapper, .grid__item"
        );

        let handle = "";
        let cardViewportTop = null;

        if (card) {
          handle =
            card.getAttribute(
              "data-wishlist-item"
            ) || "";

          cardViewportTop =
            card.getBoundingClientRect().top;
        }

        const loadedPages =
          parseInt(
            grid.dataset.infiniteLoadedPages || "1",
            10
          );

        saveState({
          productUrl: normalizeProductUrl(link.href),
          productHandle: handle,
          cardViewportTop: cardViewportTop,

          scrollY:
            window.pageYOffset ||
            document.documentElement.scrollTop ||
            0,

          loadedPages: loadedPages
        });
      },
      true
    );
  }

  /*
   * ============================================================
   * INFINITE SCROLL
   * ============================================================
   */

  class InfiniteScroll extends HTMLElement {
    constructor() {
      super();

      this.anchor = this.querySelector("a");

      if (!this.anchor) {
        return;
      }

      this.loading = false;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (
              entry.isIntersecting &&
              !this.loading &&
              !window.__aldoPlpRestoring
            ) {
              this.loadNextPage();
            }
          });
        },
        {
          root: null,
          threshold: 0
        }
      );

      this.observer.observe(this);
    }

    async loadNextPage(options = {}) {
      if (this.loading) {
        return false;
      }

      const restoring =
        options.restoring === true;

      this.loading = true;

      if (this.observer) {
        this.observer.disconnect();
      }

      if (!restoring && this.anchor) {
        this.anchor.style.display = "flex";
        this.anchor.innerText = "Loading...";
      }

      const url =
        this.anchor &&
        this.anchor.getAttribute("href");

      if (!url) {
        this.loading = false;
        return false;
      }

      try {
        const response = await fetch(url, {
          credentials: "same-origin"
        });

        if (!response.ok) {
          throw new Error(
            "HTTP " + response.status
          );
        }

        const text =
          await response.text();

        const html =
          new DOMParser().parseFromString(
            text,
            "text/html"
          );

        /*
         * Grab products from the next page.
         */
        const newGrid =
          html.querySelector(
            "[data-product-grid]"
          );

        const grid =
          document.querySelector(
            "[data-product-grid]"
          );

        if (!newGrid || !grid) {
          throw new Error(
            "Product grid not found"
          );
        }

        /*
         * Append products.
         */
        Array.from(
          newGrid.children
        ).forEach((child) => {
          grid.appendChild(child);
        });

        /*
         * Count how many Shopify pages currently
         * exist in the DOM.
         */
        const currentPages =
          parseInt(
            grid.dataset.infiniteLoadedPages || "1",
            10
          );

        grid.dataset.infiniteLoadedPages =
          String(currentPages + 1);

        /*
         * Let other custom scripts know that new
         * product cards were added.
         */
        document.dispatchEvent(
          new CustomEvent(
            "aldo:infinite-products-loaded",
            {
              detail: {
                page: currentPages + 1,
                restoring: restoring
              }
            }
          )
        );

        /*
         * Get the next infinite-scroll element
         * returned by Shopify.
         */
        const newInfinite =
          html.querySelector(
            "infinite-scroll"
          );

        if (newInfinite) {
          this.replaceWith(newInfinite);
        } else {
          this.remove();
        }

        return true;
      } catch (err) {
        console.error(
          "InfiniteScroll error:",
          err
        );

        /*
         * Re-enable observer after an error.
         */
        this.loading = false;

        if (
          this.observer &&
          document.contains(this)
        ) {
          this.observer.observe(this);
        }

        if (this.anchor) {
          this.anchor.style.display = "none";
          this.anchor.innerText = "";
        }

        return false;
      }
    }
  }

  /*
   * ============================================================
   * REGISTER ELEMENT
   * ============================================================
   */

  if (!customElements.get("infinite-scroll")) {
    customElements.define(
      "infinite-scroll",
      InfiniteScroll
    );
  }

  /*
   * ============================================================
   * RESTORE ALL PREVIOUSLY LOADED PAGES
   * ============================================================
   */

  async function restoreLoadedPages(state) {
    if (!state) return;

    const targetPages =
      Math.max(
        1,
        parseInt(state.loadedPages || "1", 10)
      );

    const grid =
      document.querySelector(
        "[data-product-grid]"
      );

    if (!grid) {
      return;
    }

    /*
     * Initial Shopify page = page 1.
     */
    let currentPages =
      parseInt(
        grid.dataset.infiniteLoadedPages || "1",
        10
      );

    /*
     * Load pages sequentially.
     *
     * IMPORTANT:
     * Each fetch replaces <infinite-scroll>
     * with the next page's infinite-scroll.
     *
     * Therefore we locate the new element
     * again after every page.
     */
    while (currentPages < targetPages) {
      const infinite =
        document.querySelector(
          "infinite-scroll"
        );

      if (
        !infinite ||
        typeof infinite.loadNextPage !== "function"
      ) {
        break;
      }

      const success =
        await infinite.loadNextPage({
          restoring: true
        });

      if (!success) {
        break;
      }

      currentPages =
        parseInt(
          grid.dataset.infiniteLoadedPages || "1",
          10
        );
    }
  }

  /*
   * ============================================================
   * FULL BACK RESTORATION
   * ============================================================
   */

  async function restorePLP() {
    if (window.__aldoPlpRestoring) {
      return;
    }

    const state = getState();

    if (!state) {
      return;
    }

    window.__aldoPlpRestoring = true;

    /*
     * Stop Safari from fighting our restoration.
     */
    if ("scrollRestoration" in history) {
      history.scrollRestoration = "manual";
    }

    try {
      /*
       * First rebuild all infinite-scroll pages.
       */
      await restoreLoadedPages(state);

      /*
       * Wait two animation frames for layout.
       */
      await new Promise((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(resolve);
        });
      });

      /*
       * First restoration.
       */
      restorePosition(state);

      /*
       * Safari correction #1.
       *
       * Lazy-loaded product images may have
       * affected layout.
       */
      setTimeout(() => {
        restorePosition(state);
      }, 250);

      /*
       * Safari correction #2.
       */
      setTimeout(() => {
        restorePosition(state);
      }, 700);

      /*
       * Final correction for slower iPhones /
       * slower image rendering.
       */
      setTimeout(() => {
        restorePosition(state);

        window.__aldoPlpRestoring = false;

        if ("scrollRestoration" in history) {
          history.scrollRestoration = "auto";
        }
      }, 1200);

    } catch (error) {
      console.error(
        "PLP restoration error:",
        error
      );

      window.__aldoPlpRestoring = false;

      if ("scrollRestoration" in history) {
        history.scrollRestoration = "auto";
      }
    }
  }

  /*
   * ============================================================
   * INITIAL SETUP
   * ============================================================
   */

  setupProductClickTracking();

  document.addEventListener(
    "DOMContentLoaded",
    function () {
      const grid =
        document.querySelector(
          "[data-product-grid]"
        );

      if (grid) {
        /*
         * Initial server-rendered collection = page 1.
         */
        if (
          !grid.dataset.infiniteLoadedPages
        ) {
          grid.dataset.infiniteLoadedPages = "1";
        }
      }

      /*
       * Standard Back/Forward navigation where Safari
       * recreated the document.
       */
      if (isBackForwardNavigation()) {
        restorePLP();
      }
    }
  );

  /*
   * ============================================================
   * iOS SAFARI BFCache
   * ============================================================
   *
   * This is particularly important for iPhone.
   *
   * Safari can restore the existing DOM directly from
   * its back-forward cache. In that case DOMContentLoaded
   * does NOT necessarily run again.
   * ============================================================
   */

  window.addEventListener(
    "pageshow",
    function (event) {
      const state = getState();

      if (!state) {
        return;
      }

      /*
       * persisted = Safari restored the document
       * from BFCache.
       */
      if (event.persisted) {
        /*
         * The dynamically loaded products may already
         * still exist.
         *
         * Therefore don't blindly duplicate them.
         * restorePLP() checks loadedPages first.
         */

        window.__aldoPlpRestoring = false;

        restorePLP();
      }
    }
  );

})();